package org.teddapp.project.sync

import com.tedd.db.TeddDatabase
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import org.teddapp.project.db.nowMillis
import org.teddapp.project.server.ServerException
import org.teddapp.project.server.ServerSession

/**
 * Les octets des fichiers joints. La ligne `attachment` voyage avec la synchronisation ;
 * le fichier, lui, passe par le stockage du serveur, en trois temps : une adresse
 * signée, le dépôt, la confirmation.
 *
 * Un fichier ne part qu'une fois sa ligne connue du serveur : il refuserait sinon une
 * adresse pour un fichier qu'il ne connaît pas.
 */
class FileTransfer(private val db: TeddDatabase, private val session: ServerSession) {
    private val uploading = Mutex()

    /** Dépose ce qui attend. Rend le nombre de fichiers envoyés ; un échec n'arrête pas les autres. */
    suspend fun uploadPending(): Int = uploading.withLock {
        var sent = 0
        for (a in db.attachmentQueries.selectToUpload().executeAsList()) {
            val bytes = db.fileBlobQueries.selectById(a.id).executeAsOneOrNull() ?: continue
            val id = serverId(a.id)
            val ok = runCatching {
                val target = session.authed { api, access -> api.fileUploadUrl(access, id) }
                session.authed { api, _ -> api.putSigned(target.url, bytes, a.mime_type) }
                session.authed { api, access -> api.fileComplete(access, id) }
            }.recoverCatching { e ->
                // Déjà envoyé par une tentative coupée avant la réponse : c'est réussi.
                if (e is ServerException && e.code == "already_uploaded") Unit else throw e
            }
            if (ok.isSuccess) sent++
        }
        sent
    }

    /** Le fichier depuis le stockage. */
    suspend fun download(id: String): ByteArray {
        val sid = serverId(id)
        val target = session.authed { api, access -> api.fileDownloadUrl(access, sid) }
        return session.authed { api, _ -> api.getSigned(target.url) }
    }

    private fun serverId(local: String) = if (ServerIds.isUuid(local)) local.lowercase() else ServerIds.derive(local)
}

/**
 * Où l'écran trouve les octets d'un fichier joint : sur l'appareil s'ils y sont (en
 * attente d'envoi, ou déjà téléchargés), sinon au stockage du serveur.
 */
object TeddFiles {
    /** Le transfert du compte connecté ; null hors connexion au serveur. */
    var transfer: FileTransfer? = null

    /**
     * Garder sur l'appareil ce qu'on télécharge. Non dans le navigateur : sa base est
     * réécrite en entier à chaque enregistrement, des photos l'alourdiraient d'autant.
     */
    var keepDownloads: Boolean = true

    // Le navigateur garde quand même les dernières images vues, en mémoire.
    private val memory = LinkedHashMap<String, ByteArray>()
    private var memoryBytes = 0L
    private const val MEMORY_LIMIT = 40L * 1024 * 1024

    /** Les octets du fichier [id], ou null s'ils sont introuvables (hors ligne, pas encore envoyé ailleurs). */
    suspend fun bytes(db: TeddDatabase, id: String): ByteArray? {
        memory[id]?.let { return it }
        db.fileBlobQueries.selectById(id).executeAsOneOrNull()?.let { return it }
        val fetched = runCatching { transfer?.download(id) }.getOrNull() ?: return null
        if (keepDownloads) db.fileBlobQueries.put(id, fetched, nowMillis()) else remember(id, fetched)
        return fetched
    }

    private fun remember(id: String, bytes: ByteArray) {
        memory[id] = bytes
        memoryBytes += bytes.size
        val it = memory.entries.iterator()
        while (memoryBytes > MEMORY_LIMIT && it.hasNext()) {
            val e = it.next()
            if (e.key == id) continue
            memoryBytes -= e.value.size
            it.remove()
        }
    }
}

/**
 * Le moteur de synchronisation, suivi de l'envoi des fichiers : une fois les lignes
 * poussées, le serveur connaît les fichiers à recevoir.
 */
internal class FileAwareSyncEngine(
    private val engine: SyncEngine,
    private val files: FileTransfer,
) : SyncEngine by engine {
    override suspend fun sync(): SyncResult {
        val result = engine.sync()
        // Un fichier qui ne part pas (réseau, quota) repartira au prochain cycle.
        runCatching { files.uploadPending() }
        return result
    }
}
